// Core
import React from 'react';

// Libraries
import { useTheme } from 'styled-components';

// Hooks
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { useOverflowTooltip } from '../../../hooks/useOverflowTooltip/useOverflowTooltip';

// Types
import { Theme } from '../../../types/theme';

// Components
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../Paragraph';
import { Tooltip } from '../Tooltip';

// Component
import {
  Container,
  Content,
  DescriptionContainer,
  QuantityContainer,
} from './QuantityCard.style';
import { QuantityCardProps, QuantityStatus } from './QuantityCard.types';

function QuantityCard({
  className,
  description,
  quantity,
  status = QuantityStatus.neutral,
  testId,
}: QuantityCardProps): JSX.Element {
  // Dependencies
  const theme: Theme = useTheme();
  const { isXs } = useBreakpoint();

  const {
    containerRef,
    contentRef,
    isTooltipVisible,
  } = useOverflowTooltip({
    overflowCheckTriggers: description,
    widthOffset: 8,
  });

  return (
    <Container
      data-testid={testId}
      className={className}
    >
      <QuantityContainer status={status}>
        <Paragraph
          color={status === QuantityStatus.neutral
            ? theme.color.quantityCard.quantityNeutralColor
            : theme.color.quantityCard.quantityColor}
          size={
            isXs
              ? ParagraphSize.xs
              : ParagraphSize.sm
          }
          weight={ParagraphWeight.bold}
        >
          {quantity}
        </Paragraph>
      </QuantityContainer>
      <Content ref={containerRef}>
        {isTooltipVisible ? (
          <Tooltip text={description}>
            <DescriptionContainer ref={contentRef}>
              <Paragraph
                size={
                  isXs
                    ? ParagraphSize.xs
                    : ParagraphSize.sm
                }
                weight={ParagraphWeight.medium}
                preventOverflow
              >
                {description}
              </Paragraph>
            </DescriptionContainer>
          </Tooltip>
        ) : (
          <DescriptionContainer ref={contentRef}>
            <Paragraph
              size={
                isXs
                  ? ParagraphSize.xs
                  : ParagraphSize.sm
              }
              weight={ParagraphWeight.medium}
              preventOverflow
            >
              {description}
            </Paragraph>
          </DescriptionContainer>
        )}
      </Content>
    </Container>
  );
}

export { QuantityCard };
